import React from "react";
import PropTypes from "prop-types";
import Badge from "../../../../components/ui-components/Badge.jsx";
import AutoGrowTextarea from "./AutoGrowTextarea";
import { fmtEurSigned } from "../../blocks/format";

// Driver `category` (SP2 Task 4/7's computed Volume/Rate classification, internal/
// boardfigures/pvm.go's DecomposePVM) - display copy + Badge status-key mapping, mirroring
// DriverTableBlock.jsx's categoryLabel/CATEGORY_TONE_CLASS for the printed report so the two
// surfaces agree on Volume/Rate wording. "info" (blue-ish) for volume, "neutral" (grey) for
// rate keeps them visually distinct via Badge's existing tone vocabulary rather than adding a
// new one here; a future new/dropped/one_off code falls back to a generic title-cased label at
// "neutral" tone.
const CATEGORY_LABELS = { volume: "Volume", rate: "Rate" };
const CATEGORY_BADGE_STATUS = { volume: "info", rate: "neutral" };

function categoryLabel(category) {
  if (!category) return null;
  if (CATEGORY_LABELS[category]) return CATEGORY_LABELS[category];
  return category
    .split("_")
    .filter(Boolean)
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ");
}

// DRIVER_FIELD_META - the four editable annotation fields, ordered as the board narrative reads:
// Cause (what happened) -> Expected (does it recur) -> Action (what we'll do) -> Owner (who's
// accountable, closing metadata). Each carries an example-style placeholder so an empty field prompts
// the reviewer rather than showing a blank box. The three narrative fields are prose auto-grow
// textareas; `owner` is a short single-line input.
const DRIVER_FIELDS = ["cause", "expected", "action", "owner"];
const DRIVER_FIELD_META = {
  cause: { label: "Cause", placeholder: "What drove this change?", prose: true },
  expected: { label: "Expected", placeholder: "Will it recur, or was it one-off?", prose: true },
  action: { label: "Action", placeholder: "What's the next step, if any?", prose: true },
  owner: { label: "Owner", placeholder: "e.g. Platform team", prose: false },
};

// Shared input styling - filled, comfortably padded, with a clear focus state, so the fields read as
// part of the app's form surface instead of floating outline boxes. Width is handled separately
// (w-full is a no-op under this project's overridden width scale): prose textareas via
// AutoGrowTextarea's `fullWidth`, the owner input via an inline style below.
const FIELD_CLASS =
  "px-3 py-2 rounded-ctrl border border-border bg-background-grey text-xs leading-relaxed text-text placeholder:text-text-3 focus:outline-none focus:border-primary focus:bg-card disabled:text-text-3";

// impactTone - positive impact worsened spend (adverse/red), negative helped (favorable/green).
function impactTone(impact) {
  if (typeof impact !== "number" || impact === 0) return "text-text-2";
  return impact > 0 ? "text-danger" : "text-success";
}

function formatImpact(impact) {
  return typeof impact === "number" ? fmtEurSigned(impact) : impact ?? "—";
}

/**
 * DriverAnnotations - the driver-annotation rows: driver name/impact/category are read-only
 * (computed upstream by boardfigures/pvm.go's DecomposePVM), cause/expected/owner/action are
 * editable per-driver notes. Extracted from NarrativeRail (SP6 Task 5) so both NarrativeRail's
 * "Driver annotations" card and BoardReportInspector's driver_table field set render the same
 * rows; the caller (NarrativeRail) still owns any surrounding "Save drivers" affordance - this
 * component is just the rows.
 */
const DriverAnnotations = ({ drivers, onlyIndex, disabled, onDriverFieldChange, onFocus }) => {
  const focus = () => {
    if (onFocus) onFocus("drivers");
  };

  // onlyIndex (set when a single driver row was selected on the report canvas) narrows the list to
  // that one driver, but keeps its ORIGINAL array index so onDriverFieldChange(index, ...) still
  // targets the right record in the run's drivers array. onlyIndex == null renders every driver.
  const visible = drivers
    .map((driver, index) => ({ driver, index }))
    .filter((entry) => onlyIndex == null || entry.index === onlyIndex);

  return (
    <div className="space-y-6">
      {visible.map(({ driver, index }) => (
        // eslint-disable-next-line react/no-array-index-key
        <div key={index} className="space-y-4 pb-6 border-b border-border last:border-b-0 last:pb-0">
          {/* Header: the driver's name with its category and formatted, tone-coloured impact (a
              month-over-month delta) - the context that heads the card so the reviewer knows which
              driver they're annotating and how big its move was. */}
          <div className="flex items-start justify-between gap-3 pb-3 border-b border-border">
            <span className="text-sm font-semibold text-text leading-snug">{driver.driver}</span>
            <div className="flex flex-col items-end gap-1 flex-shrink-0">
              <div className="flex items-center gap-2">
                {categoryLabel(driver.category) && (
                  <Badge
                    label={categoryLabel(driver.category)}
                    status={CATEGORY_BADGE_STATUS[driver.category] || "neutral"}
                    size="sm"
                  />
                )}
                <span className={`text-sm font-semibold tabular-nums ${impactTone(driver.impact)}`}>
                  {formatImpact(driver.impact)}
                </span>
              </div>
              {typeof driver.impact === "number" && (
                <span className="text-[10px] uppercase tracking-wide text-text-3">vs last month</span>
              )}
            </div>
          </div>
          <div className="space-y-5">
            {DRIVER_FIELDS.map((fieldName) => {
              const meta = DRIVER_FIELD_META[fieldName];
              return (
                <label key={fieldName} className="block" htmlFor={`driver-${index}-${fieldName}`}>
                  <span className="block text-xs font-medium text-text-2 mb-1.5">{meta.label}</span>
                  {meta.prose ? (
                    <AutoGrowTextarea
                      id={`driver-${index}-${fieldName}`}
                      ariaLabel={`driver ${index + 1} ${fieldName}`}
                      placeholder={meta.placeholder}
                      className={`${FIELD_CLASS} resize-none overflow-hidden`}
                      fullWidth
                      minHeightPx={60}
                      value={driver[fieldName]}
                      disabled={disabled}
                      onChange={(v) => onDriverFieldChange(index, fieldName, v)}
                      onFocus={focus}
                    />
                  ) : (
                    <input
                      type="text"
                      id={`driver-${index}-${fieldName}`}
                      aria-label={`driver ${index + 1} ${fieldName}`}
                      placeholder={meta.placeholder}
                      className={FIELD_CLASS}
                      style={{ width: "100%" }}
                      value={driver[fieldName] || ""}
                      disabled={disabled}
                      onChange={(e) => onDriverFieldChange(index, fieldName, e.target.value)}
                      onFocus={focus}
                    />
                  )}
                </label>
              );
            })}
          </div>
        </div>
      ))}
      {visible.length === 0 && (
        <p className="text-sm text-text-3">
          {drivers.length === 0 ? "No drivers for this run." : "This driver is no longer available."}
        </p>
      )}
    </div>
  );
};

DriverAnnotations.propTypes = {
  drivers: PropTypes.arrayOf(
    PropTypes.shape({
      driver: PropTypes.string,
      impact: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
      category: PropTypes.string,
      cause: PropTypes.string,
      expected: PropTypes.string,
      owner: PropTypes.string,
      action: PropTypes.string,
    })
  ).isRequired,
  // When set, render only the driver at this original array index (single-row selection); null/
  // undefined renders every driver.
  onlyIndex: PropTypes.number,
  disabled: PropTypes.bool,
  onDriverFieldChange: PropTypes.func.isRequired,
  onFocus: PropTypes.func,
};

export default DriverAnnotations;
